import { ompApiClient } from "@app/config/axios/clients/ompApiClient";
import type { RequestConfig } from "@app/config/axios/http";
import type {
    ApiRecommendedStorePlaceData,
    ApiSpMovingData,
    ApiStorePlaceData,
    ApiStorePlaceRemainderData,
    SpMovingFilterData,
    SpMovingInsertData,
    StorePlaceCell,
    StorePlaceFilter,
    StorePlaceRemainderFilter,
} from "./types";

interface StorePlacesResponse {
    data: ApiStorePlaceData[];
}

interface StorePlaceRemaindersResponse {
    data: ApiStorePlaceRemainderData[];
}

interface StorePlaceRecommendedCellsResponse {
    data: ApiRecommendedStorePlaceData[];
}

interface SpMovingListResponse {
    data: ApiSpMovingData[];
}

interface SpMovingInsertResponse {
    outData: ApiSpMovingData;
}

/** Транспортный слой OMP — места хранения и размещение партий. */
export const storePlacesApi = {
    /**
     * Загружает места хранения по фильтру (`stock/storePlaces.loadByFilter`).
     * @param params фильтр мест хранения
     * @param config опции HTTP-запроса
     */
    load: async (
        params: {
            filter?: StorePlaceFilter;
        },
        config?: RequestConfig,
    ): Promise<ApiStorePlaceData[]> => {
        const data = await ompApiClient.postOmega<StorePlacesResponse>("stock/storePlaces.loadByFilter", params, config);
        return data.data;
    },

    /**
     * Загружает остатки по местам хранения.
     * @param params фильтр остатков
     * @param config опции HTTP-запроса
     */
    loadRemainders: async (
        params: {
            filter?: StorePlaceRemainderFilter;
        },
        config?: RequestConfig,
    ): Promise<ApiStorePlaceRemainderData[]> => {
        const data = await ompApiClient.postOmega<StorePlaceRemaindersResponse>(
            "stock/storePlaces.loadRemaindersByFilter",
            params,
            config,
        );
        return data.data;
    },

    /**
     * Загружает рекомендованные ячейки для партий (`stock/storePlaces.getRecommendedCellsForLot`). \
     * Request: `{ lotCodes, nCellsToFind }` (`nCellsToFind` — на каждую партию). \
     * Response: `{ data: ApiRecommendedStorePlaceData[] }` — пары `{ LotCode, StorePlace }`.
     * @param params коды партий и лимит ячеек на партию
     * @param config опции HTTP-запроса
     */
    getRecommendedCellsForLot: async (
        params: {
            /** Коды партий. Wire-поле `lotCodes`. */
            lotCodes: number[];
            /** Число рекомендованных ячеек на каждую партию. Wire-поле `nCellsToFind`. */
            nCellsToFind: number;
        },
        config?: RequestConfig,
    ): Promise<ApiRecommendedStorePlaceData[]> => {
        const data = await ompApiClient.postOmega<StorePlaceRecommendedCellsResponse>(
            "stock/storePlaces.getRecommendedCellsForLot",
            params,
            config,
        );
        return data.data;
    },

    /**
     * Загружает рекомендованные ячейки по заказу (`stock/storePlaces.getRecommendedCellsForOrder`). \
     * Request wire: `{ divisionCode, stockobjCode, orderCode, nCellsToFind }`. \
     * Response wire: `{ data: ApiStorePlaceData[] }`. \
     * Пачечной загрузки нет — вызывать на каждый ключ заказ/ТМЦ/подразделение.
     * @param params подразделение, ТМЦ, заказ и лимит ячеек
     * @param config опции HTTP-запроса
     */
    getRecommendedCellsForOrder: async (
        params: {
            /** Код подразделения (склада). Wire-поле `divisionCode`. */
            divisionCode: number;
            /** Код ТМЦ. Wire-поле `stockobjCode`. */
            stockobjCode: number;
            /** Код заказа. Wire-поле `orderCode`. */
            orderCode: number;
            /** Число рекомендованных ячеек. Wire-поле `nCellsToFind`. */
            nCellsToFind: number;
        },
        config?: RequestConfig,
    ): Promise<ApiStorePlaceData[]> => {
        const data = await ompApiClient.postOmega<StorePlacesResponse>(
            "stock/storePlaces.getRecommendedCellsForOrder",
            params,
            config,
        );
        return data.data;
    },

    /**
     * Загружает структуру мест хранения склада.
     * @param params фильтр мест хранения
     * @param config опции HTTP-запроса
     */
    loadCells: async (
        params: {
            filter?: StorePlaceFilter;
        },
        config?: RequestConfig,
    ): Promise<StorePlaceCell[]> => {
        const data = await ompApiClient.postOmega<StorePlacesResponse>("stock/storePlaces.loadByFilter", params, config);
        return data.data.map(serializeStorePlace);
    },

    /** Движения по местам хранения — transport `stock/storePlaces/spMoving.*`. */
    spMoving: {
        /**
         * Создаёт движение по местам хранения.
         * @param params данные движения
         * @param config опции HTTP-запроса
         */
        insert: async (params: { data: SpMovingInsertData }, config?: RequestConfig): Promise<ApiSpMovingData> => {
            const result = await ompApiClient.postOmega<SpMovingInsertResponse>(
                "stock/storePlaces/spMoving.insert",
                params,
                config,
            );
            return result.outData;
        },

        /**
         * Загружает движения по местам хранения по фильтру.
         * @param params фильтр движений
         * @param config опции HTTP-запроса
         */
        loadByFilter: async (
            params: { filter?: SpMovingFilterData },
            config?: RequestConfig,
        ): Promise<ApiSpMovingData[]> => {
            const result = await ompApiClient.postOmega<SpMovingListResponse>(
                "stock/storePlaces/spMoving.loadByFilter",
                params,
                config,
            );
            return result.data;
        },

        /**
         * Удаляет движение по местам хранения.
         * @param params код движения
         * @param config опции HTTP-запроса
         */
        remove: async (params: { code: number }, config?: RequestConfig): Promise<void> => {
            await ompApiClient.postOmega("stock/storePlaces/spMoving.remove", params, config);
        },
    },
} as const;

function serializeStorePlace(storePlace: ApiStorePlaceData): StorePlaceCell {
    return {
        Code: storePlace.Code,
        Name: storePlace.Num,
    };
}
